import { Alert, Box, Button, Checkbox, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField, Typography, Tooltip } from '@mui/material';
import Fade from '@mui/material/Fade';
import InfoIcon from '@mui/icons-material/Info';
import './YourDetails.css';
import '../../../css/Mpa.css';
import { SetStateAction, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { MerchantInformation } from '../../../Models/MerchantInformation';
import NumberFormat from 'react-number-format';
import axios from 'axios';
import config from "../../../config/config.json";
import { PageStatus } from '../MpaStepper';
import lodash from 'lodash';

interface Values {
    businessName: string;
    abn: string;
    legalName: string;
    firstName: string;
    lastName: string;
    positionTitle: string;
    street: string;
    state: string;
    suburb: string;
    postcode: string;
    fullAddress: string;
    email: string;
    emailConfirmation: string;
    phone: string;
    acquirer: string;
    posProvider: string;
    merchantId: string;
    terminalIds: string;
    restrictedItemsSold: string;
    identity: boolean;
    authorisation: boolean;
}

export interface YourDetailsProps {
    merchantInformation: MerchantInformation,
    pageStatus: SetStateAction<PageStatus>;
    handleNext: (capturedInformation: MerchantInformation) => void
}

const len = config.INPUT_FIELD_LEN;

export const YourDetails = (props: YourDetailsProps) => {
    const { merchantInformation, pageStatus, handleNext } = props;
    const [abnNotFound, setAbnNotFound] = useState(false);
    const [abnServiceDown, setAbnServiceDown] = useState(false);
    const [legalEntityName, setLegalEntityName] = useState<string>(merchantInformation.legalName);
    const [abnCancelled, setAbnCancelled] = useState(false);

    function getPageStatus() {
        if (pageStatus === PageStatus.ValidState) {
            return true;
        }
        return false;
    }

    const [abnOutcomePermitted, setAbnOutcomePermitted] = useState(() =>
        getPageStatus()
    );

    const handleAbnChange = useCallback(
        () => {
            setAbnOutcomePermitted(false);
            setAbnNotFound(false);
            setAbnServiceDown(false);
            setLegalEntityName('');
            setAbnCancelled(false);
        },
        [],
    )

    const scrollToTop = useCallback(
        () => {
            globalThis.scrollTo(0,0);
        },
        [],
    )

    const getLegalName = useCallback((xmlDoc: Document) => {
            if (xmlDoc.getElementsByTagName("entityTypeCode")[0].textContent === "IND") {
                try {
                    const givenName = xmlDoc.getElementsByTagName("givenName")[0].textContent;
                    const otherGivenName = xmlDoc.getElementsByTagName("otherGivenName")[0].textContent;
                    const familyName = xmlDoc.getElementsByTagName("familyName")[0].textContent;

                    setLegalEntityName(`${familyName || ''}${familyName && (givenName || otherGivenName) ? ', ' : ''}${givenName || ''}${givenName && otherGivenName ? ' ' : ''}${otherGivenName || ''}`);
                } catch (error : unknown){
                    console.error('Cannot set legal entity name (individual) ', String(error))
                }
            } else {
                try {
                    const organisationName = xmlDoc.getElementsByTagName("mainName")[0].getElementsByTagName("organisationName")[0].textContent;
                    setLegalEntityName(organisationName || '');
                } catch (error : unknown){
                    console.error('Cannot set legal entity name (organisation) ', String(error))
                }
            }
        }
        ,
        []
    )

    const checkAbnCancelled = useCallback((xmlDoc: Document) => {
            try {
                let status = xmlDoc.getElementsByTagName("entityStatusCode")[0].textContent;
                if (status === 'Cancelled'){
                    setAbnCancelled(true);
                }
            } catch (error : unknown) {
                console.log('Error when checking for cancelled ABN', String(error));
            }
        }
        ,
        []
    )

    const verifyAbn = useCallback((abn: string) => {
            // reset ABN service down prior checking
            setAbnServiceDown(false);

            //
            const abnUrl = abn.trim().replace(' ', '+');
            const abnSearchUrl = config.ABN_URL_BASE + config.ABN_SEARCH_PARAM + abnUrl + config.INCLUDE_HIST_STRING + config.AUTH_GUID_STRING;
            axios.get(abnSearchUrl, { timeout: 10000 })
            .then((response) => {

                const xmlDoc = (new DOMParser()).parseFromString(response.data, "text/xml");

                if (xmlDoc.getElementsByTagName("exception")[0]) {
                    setAbnNotFound(true);
                } else {
                    setAbnOutcomePermitted(true);
                    getLegalName(xmlDoc);

                    checkAbnCancelled(xmlDoc);

                }

            }, (error) => {
                console.log(error);
                setAbnServiceDown(true);
                setAbnOutcomePermitted(true);
            });
        },
        [checkAbnCancelled, getLegalName],
    )

    const formik = useFormik({
        initialValues: {
            businessName: merchantInformation.businessName,
            abn: merchantInformation.abn,
            legalName: merchantInformation.legalName,
            firstName : merchantInformation.firstName,
            lastName : merchantInformation.lastName,
            positionTitle: merchantInformation.positionTitle,
            street: merchantInformation.street,
            state: merchantInformation.state,
            suburb: merchantInformation.suburb,
            postcode: merchantInformation.postcode,
            fullAddress: merchantInformation.fullAddress,
            email: merchantInformation.email,
            emailConfirmation: merchantInformation.email,
            phone: merchantInformation.phone,
            acquirer: merchantInformation.acquirer,
            posProvider: merchantInformation.posProvider,
            merchantId: merchantInformation.merchantId,
            terminalIds: merchantInformation.terminalIds,
            restrictedItemsSold: merchantInformation.restrictedItemsSold,
            identity: merchantInformation.identity,
            authorisation: merchantInformation.authorisation
        },
        validationSchema: yup.object({
            // mandatory
            businessName: yup.string()
                .max(100, 'Business trading name cannot exceed 100 characters in length.')
                .matches(/\S/, 'Field cannot contain only whitespace characters. Please enter your business trading name.')
                .required('Please enter your business trading name.'),
            abn: yup.string()
                .max(14, 'Please enter a valid ABN.')
                .matches(/\S/, 'Field cannot contain only whitespace characters. Please enter your ABN.')
                .required('Please enter your ABN.')
                .transform((value) => {
                    return value === null ? value : value.replaceAll(' ', "");
                    })
                .matches(/^\d{11}$/, 'Please enter a valid ABN.'),
            firstName: yup.string()
                .max(50, 'First name cannot exceed 50 characters in length.')
                .matches(/\S/, 'Field cannot contain only whitespace characters. Please enter your first name.')
                .required('Please enter your first name.'),
            lastName: yup.string()
                .max(50, 'Last name cannot exceed 50 characters in length.')
                .matches(/\S/, 'Field cannot contain only whitespace characters. Please enter your last name.')
                .required('Please enter your last name.'),
            positionTitle: yup.string()
                .max(50, 'Position title cannot exceed 50 characters in length. ')
                .matches(/\S/, 'Field cannot contain only whitespace characters. Please enter your position title.')
                .required('Please enter your position title.'),
            street: yup.string()
                .max(100, 'Street address cannot exceed 100 characters in length.')
                .matches(/\S/, 'Field cannot contain only whitespace characters. Please enter your business street address.')
                .required('Please enter your business street address.'),
            suburb: yup.string()
                .max(50, 'Suburb cannot exceed 50 characters in length.')
                .matches(/\S/, 'Field cannot contain only whitespace characters. Please enter your business suburb.')
                .required('Please enter your business suburb.'),
            state: yup.string()
                .required('Please enter your business state.'),
            postcode: yup.string()
                .max(4, 'Please enter a valid postcode.')
                .required('Please enter your business postcode.')
                .matches(/^\d{4}$/, 'Please enter a valid postcode.'),
            email: yup.string()
                .max(320, 'Email address cannot exceed 320 characters in length.')
                .required('Please enter your email address.')
                .email('Please enter a valid email address.'),
            emailConfirmation: yup.string()
                .oneOf([yup.ref('email')], 'Your email address does not match.')
                .required('Please re-enter your email address.'),
            phone: yup.string()
                .required('Please enter your phone number.')
                .transform((value) => {
                    return value === null ? value : value.replaceAll(' ', "");
                    })
                .matches(/^((\+61(2|3|4|7|8)\d{8})|(0(2|3|4|7|8)\d{8})|(((1300)|(1800))\d{6})|((13)\d{4}))$/,
                'Please enter a valid phone number using digits, spaces and + only. Please also include a valid area code if entering a landline.'),

            // optional
            acquirer: yup.string()
                .max(150, 'Acquirer cannot exceed 150 characters in length.'),
            posProvider: yup.string()
                .max(150, 'POS provider cannot exceed 150 characters in length.'),
            merchantId: yup.string()
                .max(100, 'Merchant ID cannot exceed 100 characters in length.'),
            terminalIds: yup.string()
                .max(200, 'Terminal IDs cannot exceed 50 characters in length.'),
            restrictedItemsSold: yup.string()
                .max(320, 'Restricted items sold cannot exceed 320 characters in length.'),

            // mandatory
            identity: yup.bool()
                .oneOf([true], 'Please confirm that you are the business owner or manager of the business.'),
            authorisation: yup.bool()
                .oneOf([true], 'Please confirm that you are authorised to sign the Merchant Participation Agreement.')
        }),
        validate,
        onSubmit: (values) => {

            values.fullAddress = `${values.street}, ${values.suburb}, ${values.state} ${values.postcode}`

            if (abnOutcomePermitted) handleNext(values);
            else globalThis.scrollTo(0,0);
        },
    });

    function getIdentityStatus() {
        if (formik.values.identity === true) {
            return true;
        }
        return false;
    }

    function getAuthorisationStatus() {
        if (formik.values.authorisation === true) {
            return true;
        }
        return false;
    }

    const [identityChecked, setIdentityChecked] = useState(() =>
        getIdentityStatus()
    );
    const [authorisationChecked, setAuthorisationChecked] = useState(() =>
        getAuthorisationStatus()
    );

    const handleIdentityClick = useCallback(
        () => {
            setIdentityChecked(!identityChecked);
            formik.setFieldValue('identity', !identityChecked);
        },
        [formik, identityChecked],
    )

    const handleAuthorisationClick = useCallback(
        () => {
            setAuthorisationChecked(!authorisationChecked);
            formik.setFieldValue('authorisation', !authorisationChecked);
        },
        [authorisationChecked, formik],
    )

    const openPrivatePolicy = useCallback(
        () => {
            globalThis.open(config.PRIVACY_POLICY);
        },
        [],
    )

    const mandatoryFieldsAreEmpty = useCallback(
        (input : Values) => {

            if (input.businessName.trim() === '') return true;
            if (input.abn.trim() === '') return true;
            // if (input.legalName.trim() === '') return true; -- may be empty if ABN not found
            if (input.firstName.trim()  === '') return true;
            if (input.lastName.trim()  === '') return true;
            if (input.positionTitle.trim() === '') return true;
            if (input.street.trim() === '') return true;
            if (input.state.trim() === '') return true;
            if (input.suburb.trim() === '') return true;
            if (input.postcode.trim() === '') return true;
            ///if (input.fullAddress.trim() === '') return true; -- this is not filled until submit
            if (input.email=== '') return true;
            if (input.emailConfirmation.trim() === '') return true;
            if (input.phone.trim() === '') return true;

            if (input.identity === false) return true;
            if (input.authorisation === false) return true;

            return false;
        },
        [],
    )

    function validate(values: Values) {
        const errors: any = {};

        // Show error immediately if user exceeds max length
        const checkLength = (fieldLabel: string, fieldName: string, length: number) => {
            if (lodash.get(values, fieldName).length > length) {
                lodash.set(formik.touched, fieldName, true);
                errors.fieldName = `${fieldLabel} cannot exceed ${length} characters in length.`;
            }
        }

        checkLength("Business trading name", "businessName", len.BUSINESS_NAME_LEN);
        checkLength("ABN", "abn", len.ABN_LEN);
        checkLength("First name", "firstName", len.FIRST_NAME_LEN);
        checkLength("Last name", "lastName", len.LAST_NAME_LEN);
        checkLength("Position title ", "positionTitle", len.POSITION_TITLE_LEN);
        checkLength("Street address", "street", len.STREET_LEN);
        checkLength("Suburb", "suburb", len.SUBURB_LEN);
        checkLength("Postcode", "postcode", len.POSTCODE_LEN);
        checkLength("Email address", "email", len.EMAIL_LEN);
        checkLength("Acquirer", "acquirer", len.ACQUIRER_LEN);
        checkLength("POS provider", "posProvider", len.POS_PROVIDER_LEN);
        checkLength("Merchant ID", "merchantId", len.MERCHANT_ID_LEN);
        checkLength("Terminal IDs", "terminalIds", len.TERMINAL_IDS_LEN);
        checkLength("Restricted items sold", "restrictedItemsSold", len.RESTRICTED_ITEMS_LEN);

        // Show errors immediately if illegal character is found
        function checkIllegalChars(fieldName: string) {
            const regexIllegal = /[<>;&]/;

            if (regexIllegal.test(lodash.get(values, fieldName))) {
                lodash.set(formik.touched, fieldName, true);
                errors[fieldName] = 'The following characters are not allowed in this field: < > ; &';
            }
        }

        function checkIllegalCharsBusinessName(fieldName: string) {
            const regexIllegalBusinessName = /[<>;]/;

            if (regexIllegalBusinessName.test(lodash.get(values, fieldName))) {
                lodash.set(formik.touched, fieldName, true);
                errors[fieldName] = 'The following characters are not allowed in this field: < > ;';
            }
        }

        checkIllegalCharsBusinessName("businessName");
        checkIllegalChars("abn");
        checkIllegalChars("firstName");
        checkIllegalChars("lastName");
        checkIllegalChars("positionTitle");
        checkIllegalChars("street");
        checkIllegalChars("suburb");
        checkIllegalChars("postcode");
        checkIllegalChars("email");
        checkIllegalChars("emailConfirmation");
        checkIllegalChars("phone");
        checkIllegalChars("acquirer");
        checkIllegalChars("posProvider");
        checkIllegalChars("merchantId");
        checkIllegalChars("terminalIds");
        checkIllegalChars("restrictedItemsSold");

        return errors;
    }

    const removeSpaces = useCallback(
        (string: string) => {
            return string.replaceAll(' ', "");
        },
        [],
    )

    const formatAbn = useCallback(
        (rawAbn: string) => {
            const strippedAbn = removeSpaces(rawAbn);
            return `${strippedAbn.substring(0,2)}${strippedAbn.substring(2, 5) ? ' ' + strippedAbn.substring(2, 5) : ''}${strippedAbn.substring(5, 8) ? ' ' + strippedAbn.substring(5, 8) : ''}${strippedAbn.substring(8, 11) ? ' ' + strippedAbn.substring(8, 11) : ''}${strippedAbn.substring(11)}`;
        },
        [removeSpaces],
    )

    const triggerFunctionOnEnter = useCallback(
        (trigger) => (e: React.KeyboardEvent<HTMLSpanElement>) => {
            if(e.key === "Enter") {
                trigger();
            }
        },
        [],
    )

    const preventDefault = useCallback(
        () => (e: React.KeyboardEvent<HTMLSpanElement>) => {
            if(e.key === "Enter") {
                e.preventDefault();
            }
        },
        [],
    )

    const handleChange = useCallback(
        (changeFunction) => (event: React.ChangeEvent<HTMLInputElement>) => {
            formik.handleChange(event)
            changeFunction()
        },
        [formik],
    )

    const onBlurABN = useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            formik.handleBlur(event);
            formik.values.abn = formatAbn(formik.values.abn);
            if (!formik.errors.abn) verifyAbn(event.target.value);
        },
        [formatAbn, formik, verifyAbn],
    )

    return (
        <>
            <Box className="background yourDetails_background">
                <Box className="formBox yourDetails_formBox">
                    <Typography variant="h1" className="yourDetails_heading1" sx={{mb:'0.9375rem'}}>
                        Your details
                    </Typography>
                    <Typography variant="h3" className="yourDetails_heading3" sx={{mb:'0.9375rem'}}>
                        General details about your business
                    </Typography>
                    <Typography variant="h5" className="yourDetails_heading5" sx={{mb:'2.1875rem'}}>

                        {"Please provide the following information so that we can populate your merchant participation agreement.  You will need your POS provider, Acquirer, Merchant ID and Terminal ID(s) information, if you are unsure of this information, you are able to provide this to us later."}

                    </Typography>
                    <form onSubmit={formik.handleSubmit} className="widest" onKeyDown={preventDefault}>
                        <TextField
                            className="yourDetails_textField widest"
                            id="businessName"
                            label="Business trading name*"
                            variant="filled"
                            sx={{mb:'1.25rem'}}
                            value={formik.values.businessName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.businessName && Boolean(formik.errors.businessName)}
                            helperText={formik.touched.businessName && formik.errors.businessName ? formik.errors.businessName : ''}
                        />
                        <TextField
                            className="yourDetails_textField widest"
                            id="abn"
                            label="ABN*"
                            variant="filled"
                            sx={{mb:'1.25rem'}}
                            value={formik.values.abn}
                            onChange={handleChange(handleAbnChange)}
                            onBlur={onBlurABN}
                            error={formik.touched.abn && Boolean(formik.errors.abn)}
                            helperText={formik.touched.abn && formik.errors.abn ? formik.errors.abn : ''}
                        />
                        {/* ABN found and legal name auto-filled */}
                        {abnOutcomePermitted && legalEntityName &&
                            <Alert
                                className="yourDetails_alert">
                                    ABN found
                                    <br/><span className = "yourDetails_alertDesc">Your legal entity name has been automatically entered below.</span>
                            </Alert>
                        }
                        {/* ABN found but no legal name retrieved in ABN Lookup */}
                        {abnOutcomePermitted && !legalEntityName && !abnServiceDown &&
                            <Alert
                                className="yourDetails_alert">
                                    ABN found
                                    <br/><span className = "yourDetails_alertDesc">Legal entity name data unavailable. Services Australia to enter during countersigning.</span>
                            </Alert>
                        }
                        {/* ABN not found by ABN Lookup */}
                        {abnNotFound && !formik.errors.abn &&
                            <Alert
                                severity="error"
                                className="yourDetails_alert"
                            >
                                ABN not found
                                <br/><span className = "yourDetails_alertDesc">
                                    <NumberFormat value={formik.values.abn} displayType={'text'} format="## ### ### ####" />&nbsp;is not a valid ABN
                                </span>
                            </Alert>
                        }
                        {/* ABN cancelled by ABN Lookup */}
                        {abnCancelled && !formik.errors.abn &&
                            <Alert
                                severity="error"
                                className="yourDetails_alert"
                            >
                                ABN cancelled
                                <br/><span className = "yourDetails_alertDesc">
                                    The ABN <NumberFormat value={formik.values.abn} displayType={'text'} format="## ### ### ####" />&nbsp;has been cancelled, cannot proceed, please enter an active ABN
                                </span>
                            </Alert>
                        }
                        {/* ABN Lookup not responding - users allowed to proceed without ABN verification*/}
                        {abnServiceDown &&
                            <Alert
                                severity="warning"
                                className="yourDetails_alert">
                                An unknown error occured.
                                <br/><span className = "yourDetails_alertDesc">Legal entity name data unavailable. Services Australia to enter during countersigning.</span>
                            </Alert>
                        }
                        <TextField
                            disabled
                            className="yourDetails_textField widest"
                            id="legalName"
                            label="Legal entity name"
                            variant="filled"
                            value={formik.values.legalName = legalEntityName}
                        />
                        <TextField
                            className="yourDetails_textField widest"
                            id="firstName"
                            label="First name*"
                            variant="filled"
                            value={formik.values.firstName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                            helperText={formik.touched.firstName && formik.errors.firstName ? formik.errors.firstName : ''}
                        />
                        <TextField
                            className="yourDetails_textField widest"
                            id="lastName"
                            label="Last name*"
                            variant="filled"
                            value={formik.values.lastName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                            helperText={formik.touched.lastName && formik.errors.lastName ? formik.errors.lastName : ''}
                        />
                        <TextField
                            className="yourDetails_textField widest"
                            id="positionTitle"
                            label="Position title*"
                            variant="filled"
                            value={formik.values.positionTitle}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.positionTitle && Boolean(formik.errors.positionTitle)}
                            helperText={formik.touched.positionTitle && formik.errors.positionTitle ? formik.errors.positionTitle : ''}
                        />
                        <TextField
                            className="yourDetails_textField widest"
                            id="email"
                            label="Email address*"
                            variant="filled"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
                        />
                        <TextField
                            className="yourDetails_textField widest"
                            id="emailConfirmation"
                            label="Confirm email address*"
                            variant="filled"
                            value={formik.values.emailConfirmation}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.emailConfirmation && Boolean(formik.errors.emailConfirmation)}
                            helperText={formik.touched.emailConfirmation && formik.errors.emailConfirmation ? formik.errors.emailConfirmation : ''}
                        />
                        <TextField
                            className="yourDetails_textField widest"
                            id="phone"
                            label="Phone number*"
                            variant="filled"
                            value={formik.values.phone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.phone && Boolean(formik.errors.phone)}
                            helperText={formik.touched.phone && formik.errors.phone ? formik.errors.phone: ''}
                        />
                        <Typography sx={{
                            textAlign: 'left',
                            font: 'normal normal 600 1.125rem/2rem Open Sans',
                            color: '#5F277E',
                            padding: '0.3125rem 0rem 0.625rem'
                        }}>
                            Business address
                        </Typography>
                        <TextField
                            className="yourDetails_textField widest"
                            id="street"
                            label="Street address*"
                            variant="filled"
                            value={formik.values.street}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.street && Boolean(formik.errors.street)}
                            helperText={formik.touched.street && formik.errors.street ? formik.errors.street : ''}
                        />
                        <TextField
                            className="yourDetails_textField widest"
                            id="suburb"
                            label="Suburb*"
                            variant="filled"
                            value={formik.values.suburb}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.suburb && Boolean(formik.errors.suburb)}
                            helperText={formik.touched.suburb && formik.errors.suburb ? formik.errors.suburb : ''}
                        />
                        <div className='flexAddress'>
                            <div className='yourDetails_splitRowWidth'>
                                <FormControl variant="filled" className='widest'>
                                    <InputLabel
                                        id="yourDetails_stateTerritory_label"
                                        error={formik.touched.state && Boolean(formik.errors.state)}
                                    >
                                        State*
                                    </InputLabel>
                                    <Select
                                        labelId="yourDetails_stateTerritory_label"
                                        label="State*"
                                        id="state"
                                        name="state"
                                        value={formik.values.state}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.state && Boolean(formik.errors.state)}
                                    >
                                        <MenuItem value={""}>
                                            <em>None</em>
                                        </MenuItem>
                                        <MenuItem value={'ACT'}>ACT</MenuItem>
                                        <MenuItem value={'NSW'}>NSW</MenuItem>
                                        <MenuItem value={'NT'}>NT</MenuItem>
                                        <MenuItem value={'QLD'}>QLD</MenuItem>
                                        <MenuItem value={'SA'}>SA</MenuItem>
                                        <MenuItem value={'TAS'}>TAS</MenuItem>
                                        <MenuItem value={'VIC'}>VIC</MenuItem>
                                        <MenuItem value={'WA'}>WA</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormHelperText className="yourDetails_errorText">
                                    {formik.touched.state && formik.errors.state ? formik.errors.state : ''}
                                </FormHelperText>
                            </div>
                            <TextField
                                className="yourDetails_textField yourDetails_splitRowWidth"
                                id="postcode"
                                label="Postcode*"
                                variant="filled"
                                value={formik.values.postcode}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.postcode && Boolean(formik.errors.postcode)}
                                helperText={formik.touched.postcode && formik.errors.postcode ? formik.errors.postcode : ''}
                            />
                        </div>

                        <Typography sx={{
                            textAlign: 'left',
                            font: 'normal normal 600 1.125rem/2rem Open Sans',
                            color: '#5F277E',
                            padding: '0.3125rem 0rem 0.625rem'
                        }}>
                            Business Information (required for POS and Acquirer PLB checks)
                        </Typography>

                        <Tooltip
                            title="The financial institution that processes your business' debit and card payments transactions."
                            arrow placement="bottom-end"
                            enterTouchDelay={100} enterDelay={100}
                            leaveTouchDelay={1500} leaveDelay={50}
                            slots={{transition: Fade,}}
                            slotProps={{
                                tooltip: { className: 'field-tooltip '},
                                arrow: {className: 'field-tooltip-arrow'},
                                transition: {timeout: 600},
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [50, -25],
                                            }
                                        }
                                    ]
                                }
                            }}
                        >
                            <div style={{display: "flex"}}>
                                <TextField
                                    className="yourDetails_textField widest"
                                    id="acquirer"
                                    label="Acquirer"
                                    variant="filled"
                                    value={formik.values.acquirer}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.acquirer && Boolean(formik.errors.acquirer)}
                                    helperText={formik.touched.acquirer && formik.errors.acquirer ? formik.errors.acquirer : ""}
                                />
                                <InfoIcon className={'field-tooltip-info-icon'} />
                            </div>

                        </Tooltip>

                        <Tooltip
                            title="The organisation that provides the POS system enabling the sale and management of goods and services offered by your business."
                            arrow placement="bottom-end"
                            enterTouchDelay={100} enterDelay={100}
                            leaveTouchDelay={1500} leaveDelay={50}
                            slots={{transition: Fade,}}
                            slotProps={{
                                tooltip: {className: 'field-tooltip'},
                                arrow: {className: 'field-tooltip-arrow'},
                                transition: {timeout: 600},
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [50, -25],
                                            }
                                        }
                                    ]
                                }
                            }}
                            >
                                <div style={{display: "flex"}}>
                                    <TextField
                                        className="yourDetails_textField widest"
                                        id="posProvider"
                                        label="POS provider"
                                        variant="filled"
                                        value={formik.values.posProvider}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.posProvider && Boolean(formik.errors.posProvider)}
                                        helperText={formik.touched.posProvider && formik.errors.posProvider ? formik.errors.posProvider : ''}
                                    />
                                    <InfoIcon className={'field-tooltip-info-icon'} />
                                </div>

                        </Tooltip>

                        <Tooltip title="Your business' Card Acceptor Identification Code (CAIC) from industry standard AS2805. This is a number assigned to a merchant by its acquirer."
                            arrow placement="bottom-end"
                            enterTouchDelay={100} enterDelay={100}
                            leaveTouchDelay={1500} leaveDelay={50}
                            slots={{transition: Fade,}}
                            slotProps={{
                                tooltip: {className: 'field-tooltip'},
                                arrow: {className: 'field-tooltip-arrow'},
                                transition: {timeout: 600},
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [50, -25],
                                            }
                                        }
                                    ]
                                }
                            }}
                        >
                            <div style={{display: "flex"}}>
                                <TextField
                                    className="yourDetails_textField widest"
                                    id="merchantId"
                                    label="Merchant ID"
                                    variant="filled"
                                    value={formik.values.merchantId}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.merchantId && Boolean(formik.errors.merchantId)}
                                    helperText={formik.touched.merchantId && formik.errors.merchantId ? formik.errors.merchantId : ''}
                                />
                                <InfoIcon className={'field-tooltip-info-icon'} />
                            </div>

                        </Tooltip>

                        <Tooltip title="Your business' Card Acceptor Terminal Identification Code(s) (CATIC) from industry standard AS2805. This is the number assigned to a PIN pad by the acquirer."
                            arrow placement="bottom-end"
                            enterTouchDelay={100} enterDelay={100}
                            leaveTouchDelay={1500} leaveDelay={50}
                            slots={{transition: Fade,}}
                            slotProps={{
                                tooltip: {className: 'field-tooltip'},
                                arrow: {className: 'field-tooltip-arrow'},
                                transition: {timeout: 600},
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [50, -25],
                                            }
                                        }
                                    ]
                                }
                            }}
                        >
                            <div style={{display: "flex"}}>
                                <TextField
                                    className="yourDetails_textField widest"
                                    id="terminalIds"
                                    label="Terminal IDs"
                                    variant="filled"
                                    value={formik.values.terminalIds}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.terminalIds && Boolean(formik.errors.terminalIds)}
                                    helperText={formik.touched.terminalIds && formik.errors.terminalIds ? formik.errors.terminalIds : ''}
                                />
                                <InfoIcon className={'field-tooltip-info-icon'} />
                            </div>

                        </Tooltip>

                        <Tooltip
                            title="Products that your business sells which may not be purchased with a SmartCard under eIM, such as tobacco, alcohol, gambling, pornography, cash-like products. See Learn More & FAQ for further information."
                            arrow placement="bottom-end"
                            enterTouchDelay={100} enterDelay={100}
                            leaveTouchDelay={1500} leaveDelay={50}
                            slots={{transition: Fade,}}
                            slotProps={{
                                tooltip: {className: 'field-tooltip'},
                                arrow: {className: 'field-tooltip-arrow'},
                                transition: {timeout: 600},
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [50, -25],
                                            }
                                        }
                                    ]
                                }
                            }}
                        >
                            <div style={{display: "flex"}}>
                                <TextField
                                    className="yourDetails_textField widest"
                                    id="restrictedItemsSold"
                                    label="Restricted items sold"
                                    variant="filled"
                                    value={formik.values.restrictedItemsSold}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.restrictedItemsSold && Boolean(formik.errors.restrictedItemsSold)}
                                    helperText={formik.touched.restrictedItemsSold && formik.errors.restrictedItemsSold ? formik.errors.restrictedItemsSold : ''}
                                />
                                <InfoIcon className={'field-tooltip-info-icon'} />
                            </div>

                        </Tooltip>


                        <Box
                            className="yourDetails_checkBoxContainer widest"
                            tabIndex={0}
                            onKeyDown={triggerFunctionOnEnter(handleIdentityClick)}
                            onClick={handleIdentityClick}
                        >
                            <Checkbox
                                tabIndex={-1}
                                id="chkIdentity"
                                value={formik.values.identity}
                                checked={identityChecked === true}
                                sx={{ '& .MuiSvgIcon-root': { fontSize: 35 } }}
                                onBlur={formik.handleBlur}
                                onChange={handleChange(handleIdentityClick)}
                            />
                            <Typography className="yourDetails_checkBoxText">
                                I confirm I am the owner or manager of this business*
                            </Typography>
                        </Box>
                        <FormHelperText className="yourDetails_errorText">
                            {formik.touched.identity && formik.errors.identity ? formik.errors.identity : ''}
                        </FormHelperText>
                        <Box
                            className="yourDetails_checkBoxContainer widest"
                            tabIndex={0}
                            onKeyDown={triggerFunctionOnEnter(handleAuthorisationClick)}
                            onClick={handleAuthorisationClick}
                        >
                            <Checkbox
                                tabIndex={-1}
                                id="chkAuthorisation"
                                value={formik.values.authorisation}
                                checked={authorisationChecked === true}
                                sx={{ '& .MuiSvgIcon-root': { fontSize: 35 } }}
                                onBlur={formik.handleBlur}
                                onChange={handleChange(handleAuthorisationClick)}
                            />
                            <Typography className="yourDetails_checkBoxText">
                                I am authorised to sign this Merchant Participation Agreement*
                            </Typography>
                        </Box>
                        <FormHelperText className="yourDetails_errorText">
                            {formik.touched.authorisation && formik.errors.authorisation ? formik.errors.authorisation : ''}
                        </FormHelperText>

                        <Typography variant="caption" className="widest" sx={{mb:'3.125rem'}}>
                            *indicates mandatory field
                        </Typography>
                        <Typography
                            tabIndex={0}
                            onKeyDown={triggerFunctionOnEnter(openPrivatePolicy)}
                            className="yourDetails_privatePolicy"
                        >
                            By submitting this form you agree to our <span className="yourDetails_privatePolicyHighlight" onClick={openPrivatePolicy} onKeyDown={triggerFunctionOnEnter(openPrivatePolicy)}>Privacy Policy</span> which explains how we may collect, use and disclose your personal information.
                        </Typography>
                        <div className="yourDetails_nextButtonContainer widest">
                            <Button type="submit" className="primaryButton" onClick={scrollToTop}
                                    disabled={!formik.isValid || mandatoryFieldsAreEmpty(formik.values) || abnCancelled}>
                                Next
                            </Button>
                        </div>
                    </form>
                </Box>
            </Box>
        </>
    )
}
